import { useMemo } from 'react';
import { useAdminUser } from './useAdminUser';
import {
  useArchiveTodoListMutation,
  useCreateTodoListMutation,
  useGetAvailableAssigneesQuery,
  useGetTodoListsQuery,
  useGetPropertyRolesQuery,
  useSetOccurrenceItemStatusMutation,
  useUpdateTodoListMutation,
} from '@/store/api/admin/todoListsApi';
import type { TodoList } from '@/types/admin/todoLists';

/**
 * Thin wrapper over `todoListsApi` for the Admin To Do's index. Sources `propertyId` from the
 * admin user's selected property (R-A: the server has no lease-derived default), skips the query
 * until a property is selected, and re-exports the picker queries + mutations the screen needs.
 * Intentionally minimal — no table/filter/pagination state (there is no UI in this task).
 */
export const useTodoLists = (scope: 'mine' | 'all' = 'mine') => {
  const { selectedPropertyId } = useAdminUser();
  const skip = !selectedPropertyId;

  const {
    data: todoLists,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetTodoListsQuery(
    { propertyId: selectedPropertyId!, scope },
    { skip, refetchOnMountOrArgChange: true },
  );

  const { data: roles } = useGetPropertyRolesQuery(
    { propertyId: selectedPropertyId! },
    { skip },
  );
  const { data: availableAssignees } = useGetAvailableAssigneesQuery(
    { propertyId: selectedPropertyId! },
    { skip },
  );

  const [createTodoList] = useCreateTodoListMutation();
  const [updateTodoList] = useUpdateTodoListMutation();
  const [setOccurrenceItemStatus] = useSetOccurrenceItemStatusMutation();
  const [archiveTodoList] = useArchiveTodoListMutation();

  const lists: TodoList[] = useMemo(() => todoLists ?? [], [todoLists]);

  return {
    selectedPropertyId,
    scope,
    todoLists: lists,
    roles: roles ?? [],
    availableAssignees: availableAssignees ?? [],
    isLoading,
    isFetching,
    error,
    refetch,
    // Mutations
    createTodoList,
    updateTodoList,
    setOccurrenceItemStatus,
    archiveTodoList,
  };
};
